// Exports for the Auditores screen: every auditor with the complete list of PDVs assigned to
// them (Store.auditor). Same rows feed both the Excel workbook and the PDF.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadExcelSheets } from './excel';
import { getChannelLabel, getStoreChannel } from './storeChannel';

const channelOf = (store) => getChannelLabel(getStoreChannel(store));

const channelLabel = (channel) => getChannelLabel(channel) || channel || '';

const idOf = (value) => (value && typeof value === 'object' ? value._id : value);

const sortByName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' });

/**
 * Builds the export rows: one per (auditor, PDV). Auditors with no PDV still get one row so the
 * roster is complete. Stores are sorted by name inside each auditor.
 */
export function buildAuditorAssignmentRows(auditors = [], stores = []) {
    const byAuditor = new Map();
    for (const store of stores) {
        const auditorId = idOf(store?.auditor);
        if (!auditorId) continue;
        const key = String(auditorId);
        if (!byAuditor.has(key)) byAuditor.set(key, []);
        byAuditor.get(key).push(store);
    }

    const rows = [];
    for (const auditor of [...auditors].sort(sortByName)) {
        const assigned = (byAuditor.get(String(auditor._id)) || []).sort(sortByName);
        const base = {
            Auditor: auditor.name || '',
            Correo: auditor.email || '',
            'Canal auditor': channelLabel(auditor.channel),
            'Total PDVs': assigned.length,
        };
        if (!assigned.length) {
            rows.push({ ...base, PDV: '—', RIF: '', Cliente: '', 'Canal PDV': '', 'Tipo PDV': '', Estado: '' });
            continue;
        }
        for (const store of assigned) {
            rows.push({
                ...base,
                PDV: store.name || '',
                RIF: store.rif || '',
                Cliente: store.clientName || '',
                'Canal PDV': channelOf(store),
                'Tipo PDV': store.storeType || '',
                Estado: store.active === false ? 'Inactivo' : 'Activo',
            });
        }
    }
    return rows;
}

export const ASSIGNMENT_COLUMNS = ['Auditor', 'Correo', 'Canal auditor', 'PDV', 'RIF', 'Cliente', 'Canal PDV', 'Tipo PDV', 'Estado'];
const SUMMARY_COLUMNS = ['Auditor', 'Correo', 'Canal auditor', 'Total PDVs'];

function summaryRows(rows) {
    const seen = new Set();
    return rows.filter((r) => {
        const key = `${r.Auditor}|${r.Correo}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

const stamp = () => new Date().toISOString().slice(0, 10);

/** Excel: "Asignaciones" (auditor × PDV) + "Resumen" (PDV count per auditor). */
export function exportAuditorsExcel(auditors, stores) {
    const rows = buildAuditorAssignmentRows(auditors, stores);
    downloadExcelSheets(
        [
            { name: 'Asignaciones', columns: ASSIGNMENT_COLUMNS, items: rows },
            { name: 'Resumen', columns: SUMMARY_COLUMNS, items: summaryRows(rows) },
        ],
        `Auditores-PDVs-${stamp()}.xlsx`
    );
    return rows.length;
}

/** PDF: one section per auditor with a table of their PDVs. */
export function exportAuditorsPdf(auditors, stores) {
    const rows = buildAuditorAssignmentRows(auditors, stores);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 36;

    doc.setFontSize(16);
    doc.text('Auditores y PDVs asignados', margin, 40);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generado ${new Date().toLocaleString('es-VE')} · ${auditors.length} auditores`, margin, 56);
    doc.setTextColor(0);

    let y = 72;
    const groups = new Map();
    for (const r of rows) {
        const key = `${r.Auditor}|${r.Correo}`;
        if (!groups.has(key)) groups.set(key, { header: r, items: [] });
        if (r.PDV !== '—') groups.get(key).items.push(r);
    }

    for (const { header, items } of groups.values()) {
        // Start a new page when the auditor heading would land near the bottom.
        if (y > doc.internal.pageSize.getHeight() - 90) {
            doc.addPage();
            y = 40;
        }
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`${header.Auditor}`, margin, y);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(`${header.Correo} · ${header['Canal auditor']} · ${items.length} PDV${items.length === 1 ? '' : 's'}`, margin, y + 13);
        doc.setTextColor(0);

        autoTable(doc, {
            startY: y + 20,
            margin: { left: margin, right: margin },
            head: [['PDV', 'RIF', 'Cliente', 'Canal', 'Tipo', 'Estado']],
            body: items.length
                ? items.map((r) => [r.PDV, r.RIF, r.Cliente, r['Canal PDV'], r['Tipo PDV'], r.Estado])
                : [['Sin PDVs asignados', '', '', '', '', '']],
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [8, 145, 178] },
            columnStyles: {
                0: { cellWidth: (pageWidth - margin * 2) * 0.34 },
                2: { cellWidth: (pageWidth - margin * 2) * 0.26 },
            },
            didDrawPage: (data) => {
                const page = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(120);
                doc.text(`Página ${page}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 16, { align: 'right' });
                doc.setTextColor(0);
            },
        });
        y = (doc.lastAutoTable?.finalY || y) + 22;
    }

    doc.save(`Auditores-PDVs-${stamp()}.pdf`);
    return rows.length;
}
